import { ReloadOutlined } from '@ant-design/icons';
import type { SignDetail, SignRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Descriptions, Drawer, Image, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

const TYPE_META: Record<string, { text: string; color: string }> = {
  FULL: { text: '整单签收', color: 'green' },
  PART: { text: '部分签收', color: 'gold' },
  REJECT: { text: '拒收', color: 'red' }
};

/** 签收记录：现场核验电子签收单与签名影像（演-3 闭环）。 */
export default function SignList() {
  const [currentId, setCurrentId] = useState<string | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['sign', 'list'],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => hq.delivery.listSigns({ lastId: pageParam, size: PAGE_SIZE }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery<SignDetail>({
    queryKey: ['sign', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.delivery.signDetail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<SignRow> = [
    {
      title: '签收单号',
      dataIndex: 'bizNo',
      width: 210,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    { title: '配送单号', dataIndex: 'deliveryNo', width: 210, render: (value: string | null) => value || '-' },
    { title: '签收科室', dataIndex: 'signDeptName', width: 150, render: (value: string | null) => value || '-' },
    { title: '签收人', dataIndex: 'signName', width: 110, render: (value: string | null) => value || '-' },
    {
      title: '签收类型',
      dataIndex: 'signType',
      width: 110,
      render: (value: string | null) => {
        const meta = TYPE_META[value ?? ''] ?? { text: value ?? '-', color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '异常',
      dataIndex: 'exceptionFlag',
      width: 90,
      render: (value: number) => (value ? <Tag color="red">异常</Tag> : <Tag>正常</Tag>)
    },
    {
      title: '签名影像',
      dataIndex: 'signatureImg',
      width: 100,
      render: (value: string | null) =>
        value ? (
          <Image src={value} width={40} height={40} style={{ objectFit: 'cover' }} />
        ) : (
          <Typography.Text type="secondary">无</Typography.Text>
        )
    },
    { title: '签收时间', dataIndex: 'signTime', width: 160, render: (value: string) => formatDisplayTime(value) }
  ];

  return (
    <Card
      className="page-card"
      title="签收记录"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="签收记录加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<SignRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无签收记录' }}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer
        title={detail.data?.sign.bizNo ?? '签收单详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={880}
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="配送单号">{detail.data.sign.deliveryNo ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="签收科室">{detail.data.sign.signDeptName ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="签收人">{detail.data.sign.signName ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="签收方式">{detail.data.sign.signMethod ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="签收时间">{formatDisplayTime(detail.data.sign.signTime)}</Descriptions.Item>
              <Descriptions.Item label="定位">{detail.data.sign.geoPoint ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="异常说明" span={2}>
                {detail.data.sign.exceptionDesc ?? '-'}
              </Descriptions.Item>
            </Descriptions>

            <Typography.Title level={5}>电子签名与现场影像</Typography.Title>
            {detail.data.attaches.length === 0 ? (
              <Typography.Text type="secondary">未上传影像</Typography.Text>
            ) : (
              <Space wrap>
                {detail.data.attaches.map((attach) => (
                  <div key={attach.id} style={{ textAlign: 'center' }}>
                    <Image src={attach.fileUrl} width={140} height={100} style={{ objectFit: 'cover' }} />
                    <div>
                      <Typography.Text type="secondary">{attach.fileType ?? 'PHOTO'}</Typography.Text>
                    </div>
                  </div>
                ))}
              </Space>
            )}

            <Typography.Title level={5} style={{ marginTop: 16 }}>
              签收明细
            </Typography.Title>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={detail.data.items}
              columns={[
                { title: '物资', dataIndex: 'materialName', render: (value: string | null) => value || '-' },
                { title: '单位', dataIndex: 'unitName', width: 90, render: (value: string | null) => value || '-' },
                { title: '实收', dataIndex: 'signQty', width: 100, align: 'right', render: (v: number) => formatQty(v) },
                { title: '拒收', dataIndex: 'rejectQty', width: 100, align: 'right', render: (v: number) => formatQty(v) },
                { title: '异常原因', dataIndex: 'exceptionReason', render: (value: string | null) => value || '-' }
              ]}
            />
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
